import styled from '@emotion/styled'
import { AnimatedTitle } from 'app/components/Common/Animation/AnimatedTitle'
import { FadeInUp } from 'app/components/Common/Animation/FadeInUp'
import { Button, Props as ButtonProps } from 'app/components/Common/Button'
import { Image, Props as ImageProps } from 'app/components/Common/Image'
import { styles } from 'app/theme'
import { useParallax } from 'app/utils/customFunctions'
import { motion, useScroll } from 'framer-motion'
import React, { memo, useRef } from 'react'

export interface Props {
  cta?: ButtonProps
  image?: ImageProps
  label?: string
  title?: string
}

export const Location = memo(function Location({
  cta,
  image,
  label,
  title,
}: Props) {
  if (!title) {
    return null
  }

  const containerRef = useRef<any>()

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start end', '100vh'],
  })

  const y = useParallax(scrollYProgress, [-100, 100])

  return (
    <Container ref={containerRef}>
      <Background style={{ y }}>
        {image ? <Image {...image} /> : null}
      </Background>

      <Wrapper>
        {label ? <Label tag="h2" text={label} /> : null}

        {title ? <Title tag="h3" text={title} /> : null}

        {cta ? (
          <FadeInUp>
            <CTA variant="flat-invert" {...cta} />
          </FadeInUp>
        ) : null}
      </Wrapper>
    </Container>
  )
})

const Container = styled.section`
  height: 85vh;
  max-height: 47.8125rem;
  position: relative;
  overflow: hidden;
  &:before {
    content: '';
    width: 100%;
    height: 100%;
    background: ${({ theme }) => theme.colors.variants.neutralDark1};
    opacity: 0.5;
    position: absolute;
    top: 0;
    left: 0;
    z-index: 1;
  }

  @media (max-width: 1199px) {
    height: auto;
    max-height: none;
  }
`

const Background = styled(motion.div)`
  width: 100%;
  height: calc(100% + 6.25rem);
  position: relative;

  img {
    ${styles.img}
  }

  @media (max-width: 1199px) {
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    transform: none !important;
  }
`

const Wrapper = styled.div`
  max-width: 46rem;
  position: absolute;
  top: 50%;
  left: 5vw;
  z-index: 2;
  transform: translateY(-50%);

  @media (max-width: 1199px) {
    max-width: none;
    padding: 11.125rem 2rem;
    position: relative;
    top: auto;
    left: auto;
    transform: none;
  }
`

const Label = styled(AnimatedTitle)`
  ${styles.label}

  color: ${({ theme }) => theme.colors.variants.neutralLight1};

  @media (max-width: 1199px) {
    margin-bottom: 2rem;
  }
`

const Title = styled(AnimatedTitle)`
  ${styles.title.large}

  color: ${({ theme }) => theme.colors.variants.neutralLight1};
`

const CTA = styled(Button)`
  margin-top: 2rem;
`
